/**
 * LocalizedDateTimeDisplay - Date+time display with 12h/24h format support
 */

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { useUserPreferences } from '../context';
import { zonedParts } from '../utils/timezone';
import { LtrToken } from './common/LtrToken';

export type DateFormatOverride = 'dd/mm/yyyy' | 'mm/dd/yyyy' | 'yyyy-mm-dd';
export type TimeFormatOverride = '12h' | '24h';

export interface LocalizedDateTimeDisplayProps {
  date: Date | string | null | undefined;
  dateFormat?: 'short' | 'medium' | 'long' | 'full';
  /** Override the date format instead of using user preference */
  overrideDateFormat?: DateFormatOverride;
  /** Override the time format instead of using user preference */
  overrideTimeFormat?: TimeFormatOverride;
  showDate?: boolean;
  showTime?: boolean;
  showTooltip?: boolean;
  emptyText?: string;
  className?: string;
}

/**
 * Format a date with a specific date format
 */
function formatDateWithOverride(
  date: Date | string,
  format: 'short' | 'medium' | 'long' | 'full',
  dateFormat: DateFormatOverride,
  timezone: string
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  if (dateFormat === 'yyyy-mm-dd') {
    // Resolve the calendar day in the user's configured zone. The `Date` getters
    // would answer for the browser's ambient zone and drift a day for any user
    // whose configured timezone differs from it.
    const { year, month, day } = zonedParts(d, timezone);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const options: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
  };

  switch (format) {
    case 'short':
      options.day = '2-digit';
      options.month = '2-digit';
      options.year = '2-digit';
      break;
    case 'medium':
      options.day = '2-digit';
      options.month = '2-digit';
      options.year = 'numeric';
      break;
    case 'long':
      options.day = 'numeric';
      options.month = 'long';
      options.year = 'numeric';
      break;
    case 'full':
      options.weekday = 'long';
      options.day = 'numeric';
      options.month = 'long';
      options.year = 'numeric';
      break;
  }

  const dateLocale = dateFormat === 'mm/dd/yyyy' ? 'en-US' : 'en-GB';
  return new Intl.DateTimeFormat(dateLocale, options).format(d);
}

/**
 * Format time with a specific time format
 */
function formatTimeWithOverride(
  date: Date | string,
  timeFormat: TimeFormatOverride,
  locale: string,
  timezone: string
): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';

  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: timeFormat === '12h',
    timeZone: timezone,
  }).format(d);
}

export function LocalizedDateTimeDisplay({
  date,
  dateFormat = 'medium',
  overrideDateFormat,
  overrideTimeFormat,
  showDate = true,
  showTime = true,
  showTooltip = false,
  emptyText = '-',
  className,
}: LocalizedDateTimeDisplayProps) {
  const { formatDate, formatTime, formatDateTime, formatOptions } = useUserPreferences();

  if (!date) {
    return <span className={className}>{emptyText}</span>;
  }

  let displayText: string;

  if (showDate && showTime) {
    if (overrideDateFormat || overrideTimeFormat) {
      const dateStr = overrideDateFormat
        ? formatDateWithOverride(date, dateFormat, overrideDateFormat, formatOptions.timezone)
        : formatDate(date, dateFormat);
      const timeStr = overrideTimeFormat
        ? formatTimeWithOverride(date, overrideTimeFormat, formatOptions.locale, formatOptions.timezone)
        : formatTime(date);
      displayText = `${dateStr} ${timeStr}`;
    } else {
      displayText = formatDateTime(date, dateFormat);
    }
  } else if (showDate) {
    displayText = overrideDateFormat
      ? formatDateWithOverride(date, dateFormat, overrideDateFormat, formatOptions.timezone)
      : formatDate(date, dateFormat);
  } else if (showTime) {
    displayText = overrideTimeFormat
      ? formatTimeWithOverride(date, overrideTimeFormat, formatOptions.locale, formatOptions.timezone)
      : formatTime(date);
  } else {
    displayText = emptyText;
  }

  // For tooltip, show full format with time
  const tooltipText = formatDateTime(date, 'full');

  const content = <LtrToken className={className}>{displayText}</LtrToken>;

  if (showTooltip && tooltipText !== displayText) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent>
            <p>{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
}
